import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AppShell, Badge, Button, Center, Divider, Group, Loader, SegmentedControl, Text, Title } from '@mantine/core'
import { LuArrowDownZA } from 'react-icons/lu';
import { FaSortAlphaUp } from 'react-icons/fa';
import { Panel, PanelGroup } from 'react-resizable-panels';
import PaginatedBarChart from '../components/PathExplorer/PaginatedBarChart';
import { ResizablePanelHorizontalHandle } from '../components/Layout/ResizablePanelHandle';
import NodeSearchField from '../components/StandalonePathExplorer/NodeSearchField';
import { getShortestPaths } from '../services/paths';
import MetaPathTable from '../components/StandalonePathExplorer/MetaPathHeader';
import StandalonePathExplorerContext from '../providers/StandalonePathExplorerProvider';
import Header from '../components/StandalonePathExplorer/Header';
import { nodeTypes } from '../types';
import { getHardcodedColor } from '../utils';

const enum sort {
    countUp = "count-up",
    countDown = "count-down",
    lengthDown = "length-down",
    lengthUp = "length-up"
}

const sortData = (data, sortValue) => {
    switch (sortValue) {
        case sort.countDown:
            return [...data].sort((a, b) => b.count - a.count)
        case sort.countUp:
            return [...data].sort((a, b) => a.count - b.count)
        case sort.lengthDown:
            return [...data].sort((a, b) => b.actual_length - a.actual_length)
        case sort.lengthUp:
            return [...data].sort((a, b) => a.actual_length - b.actual_length)
        default:
            return data;
    }
}

const BottomPanel = ({ barChartData, selectedType }) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

    useEffect(() => {
        setDimensions((prev) => ({
            ...prev,
            width: containerRef.current!.clientWidth,
        }));
    }, []);

    useLayoutEffect(() => {
        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                setDimensions((prev) => ({
                    ...prev,
                    height: entry.contentRect.height,
                }));
            }
        });

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            if (containerRef.current) observer.unobserve(containerRef.current);
        };
    }, []);

    return (
        <div ref={containerRef} style={{ height: "70%" }}>
            <Text c='dimmed' style={{ marginBottom: 30 }}>{`Count Distribution over ${selectedType}`}</Text>
            <PaginatedBarChart data={barChartData} nodeType={selectedType} height={dimensions.height} />
        </div>
    );
}

const StandalonePathExplorer: React.FC = () => {
    const [data, setData] = useState<any>(null)
    const [leftSide, setLeftSide] = useState<any>(null)
    const [rightSide, setRightSide] = useState<any>(null)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [pathSort, setPathSort] = useState<string>(sort.countDown);
    const { selectedHeader, barChartData, selectedType, setSelectedHeader } = useContext(StandalonePathExplorerContext)

    const callgetShortestPaths = async (query: { left: string[], right: string[] }) => {
        setSelectedHeader(null)
        setIsLoading(true)

        const response = await getShortestPaths(query.left, query.right)

        const maxPathLength = Math.max(
            ...response.map(item =>
                item.node_labels.length + item.rel_types.length
            )
        );
        response.forEach(item => {
            const nodeRelCount = item.node_labels.length + item.rel_types.length;
            const emptyColumnsNeeded = maxPathLength - nodeRelCount;
            item.actual_length = item.rel_types.length
            if (emptyColumnsNeeded > 0) {
                // Calculate how many nodes and relations need to be added
                const additionalNodes = Math.ceil(emptyColumnsNeeded / 2);
                const additionalRels = Math.floor(emptyColumnsNeeded / 2);

                // Pad the node_labels and rel_types with empty strings
                item.node_labels = [...item.node_labels, ...Array(additionalNodes).fill('')];
                item.rel_types = [...item.rel_types, ...Array(additionalRels).fill('')];
            }
        });

        console.log(response)

        setData(response)

        setIsLoading(false)
    }

    useEffect(() => {
        if (data === null) return

        setData(sortData(data, pathSort))
    }, [pathSort, data])

    return (
        <AppShell
            padding="sm"
            header={{ height: 80 }}>
            <Header />
            <AppShell.Main>
                <Group>
                    <Title order={5} c="gray">Node Types</Title>
                    <Group style={{ padding: 10 }} gap={5}>
                    {Object.values(nodeTypes).map((nt) => (
                        <Badge variant='dot' color={getHardcodedColor(nt)} size='lg'>{nt}</Badge>))
                    }
                </Group>
                </Group>
                <Group style={{ padding: '1%' }}>
                    <Title order={3} c="gray">Query</Title>
                    <NodeSearchField onSelectChange={setLeftSide} />
                    <Divider label={'Shortest Paths'}
                        labelPosition="center"
                        style={{ flexGrow: 1, minWidth: 200 }}
                    />
                    <NodeSearchField onSelectChange={setRightSide} />
                    <Button
                        disabled={leftSide === null || rightSide === null || !leftSide.length || !rightSide.length}
                        variant="transparent"
                        onClick={() => {
                            callgetShortestPaths({ left: leftSide, right: rightSide })
                        }}
                    >
                        Submit
                    </Button><Divider orientation='vertical' />
                    <Text c='dimmed'>Sort by</Text>
                    <SegmentedControl
                        value={pathSort}
                        onChange={setPathSort}
                        data={[
                            {
                                value: sort.countDown,
                                label: (
                                    <Center style={{ gap: 10 }}>
                                        <LuArrowDownZA size={16} />
                                        <span>Count</span>
                                    </Center>
                                ),
                            },
                            {
                                value: sort.countUp,
                                label: (
                                    <Center style={{ gap: 10 }}>
                                        <FaSortAlphaUp size={16} />
                                        <span>Count</span>
                                    </Center>
                                ),
                            },
                            {
                                value: sort.lengthDown,
                                label: (
                                    <Center style={{ gap: 10 }}>
                                        <LuArrowDownZA size={16} />
                                        <span>Length</span>
                                    </Center>
                                ),
                            },
                            {
                                value: sort.lengthUp,
                                label: (
                                    <Center style={{ gap: 10 }}>
                                        <FaSortAlphaUp size={16} />
                                        <span>Length</span>
                                    </Center>
                                ),
                            }
                        ]} />
                </Group>
                {isLoading ?
                    <Center style={{ height: '80vh' }}><Loader /></Center> :
                    <PanelGroup direction="vertical" style={{ height: '82vh' }}>
                        <Panel minSize={50} order={1} style={{ overflowY: 'auto' }}>
                            {data && data.map((d, index) => <><MetaPathTable key={`data-${index}`} data={d} metaId={index} /><Divider /></>)}
                        </Panel>
                        <ResizablePanelHorizontalHandle />
                        {selectedHeader && barChartData && selectedType && (
                            <Panel minSize={25} defaultSize={50} order={2} style={{ padding: "1%" }}>
                                <BottomPanel barChartData={barChartData} selectedType={selectedType} />
                            </Panel>
                        )}
                    </PanelGroup>}
            </AppShell.Main>
        </AppShell>
    );
}

export default StandalonePathExplorer;
